import { useEffect, useState } from 'react';
import { invitationState2, joinState, playerNameState } from '../../../api/stores';
import { Input, Button, buttonVariants } from '@playcastdotio/Shadcn';
import { Form, Link } from 'react-router-dom';
import { faker } from '@faker-js/faker';
import { saveName } from '../../../api';
import clsx from 'clsx';

export const Invitation = () => {
  const [isValid, setIsValid] = useState(true);
  const [canJoin, setCanJoin] = useState(false);
  const [inviteData, setInviteData] = useState(invitationState2.currentState.selectedInvite);

  useEffect(() => {
    // Initial state check
    const checkInviteValidity = (invite = invitationState2.currentState.selectedInvite) => {
      if (!invite) {
        setIsValid(false);
        setCanJoin(false);
        return;
      }
      if (invite.invalid) {
        setIsValid(false);
        setCanJoin(false);
        return;
      }

      setIsValid(true);
      setInviteData(invite);

      if (invite.type === 'lobby') {
        setCanJoin(invite.manifest.lobbyOptions.canJoin);
      } else if (invite.type === 'instant') {
        setCanJoin(true);
      }
    };

    // Check initial state
    checkInviteValidity();

    // Set up listener for state changes
    const listener = invitationState2.addListener((state) => {
      checkInviteValidity(state.selectedInvite);
    });

    return () => {
      invitationState2.removeListener(listener);
    };
  }, []); // Empty dependency array since we're using currentState inside

  return isValid ? <ValidLink canJoin={canJoin} inviteData={inviteData} /> : <InvalidLink />;
};

const InvalidLink = () => {
  return (
    <div className='mt-8 flex w-full flex-col items-center justify-center gap-4'>
      <div className='bg-footer w-full space-y-4 rounded p-4 py-6 text-center shadow-md md:w-2/3 lg:w-2/5'>
        <h3 className='row-auto text-xl font-bold'>Confirm Host Status or Get New Link</h3>
        <p className='row-auto'>
          Your host link may have expired or the host has not started the session. Get a new link or confirm the host has started and
          refresh the page.
        </p>
      </div>
      <div>
        <Link to='https://playcast.io' className={clsx(buttonVariants(), 'max-w-48 text-wrap py-8 text-center')}>
          Host Your Own Session on Playcast.io
        </Link>
      </div>
    </div>
  );
};

const ValidLink = ({ canJoin, inviteData }: { canJoin: boolean; inviteData: typeof invitationState2.currentState.selectedInvite }) => {
  const [name, setName] = useState('');
  const [placeholderName] = useState(() => faker.person.firstName() + ' ' + faker.person.lastName());
  const [description, setDescription] = useState(inviteData?.description || '');

  useEffect(() => {
    // Initialize name from state or generate placeholder
    const storedName = playerNameState.currentState.name;
    setName(storedName || placeholderName);

    // Listen for player name changes
    const nameListener = playerNameState.addListener((state) => {
      if (state.name && state.name !== name) {
        setName(state.name);
      }
    });

    // Listen for invitation changes
    const inviteListener = invitationState2.addListener((state) => {
      if (state.selectedInvite?.description !== description) {
        setDescription(state.selectedInvite?.description || '');
      }
    });

    return () => {
      playerNameState.removeListener(nameListener);
      invitationState2.removeListener(inviteListener);
    };
  }, []); // Empty dependency array since we handle updates via listeners

  useEffect(() => {
    if (inviteData?.description !== description) {
      setDescription(inviteData?.description || '');
    }
  }, [inviteData]);

  const handleJoin = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    const finalName = name || placeholderName;
    playerNameState.updateState({ name: finalName });

    if (name) {
      saveName();
    }

    joinState.updateState({
      state: inviteData?.type === 'instant' ? 'connecting' : 'inQueue',
    });
  };

  return (
    <div className='grid-rows col-span-full mx-auto grid content-center justify-center gap-y-4 text-center lg:gap-y-8'>
      <p className='row-auto mx-auto my-0 text-lg italic'>Play Together Anywhere, Anytime</p>
      <h2 className='row-auto -mt-4 text-2xl font-bold lg:text-3xl'>{description}</h2>
      <div className='row-auto w-full gap-2'>
        <Form onSubmit={handleJoin}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            type='text'
            id='playerName'
            name='playerName'
            required
            placeholder={placeholderName}
            className='bg-content mb-2 h-auto w-full rounded border-none px-4 py-3 text-center text-lg shadow-sm'
          />
          <Button
            type='submit'
            className='rounded-full'
            disabled={!canJoin || !name}
            style={{
              boxShadow: 'inset 0 4px 2px rgba(251, 191, 36, 0.5), inset 0 -4px 2px rgba(146, 64, 14, 0.5), 0 4px 4px rgba(0, 0, 0, 0.4)',
              WebkitBoxShadow: 'inset 0 4px 2px rgba(251, 191, 36, 0.5), inset 0 -4px 2px rgba(146, 64, 14, 0.5)',
              MozBoxShadow: 'inset 0 4px 2px rgba(251, 191, 36, 0.5), inset 0 -4px 2px rgba(146, 64, 14, 0.5)',
            }}
          >
            {canJoin ? 'Join Now' : 'Lobby Closed'}
          </Button>
        </Form>
      </div>
    </div>
  );
};
